import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { EppoSpeciesService } from './service/eppo-species.service';

/**
 * Inline, read-only species list as text: `Tectona grandis (TEUGR), Quercus robur (QUERO)`.
 *
 * The sibling of `app-species-badge` for the places that have room for names — report cards, the
 * DDS confirm dialog, the archive. Those printed the bare EPPO codes (`QUR313`), which nobody
 * outside a plant-health registry can read (app#254).
 *
 * Unlike the badge this resolves on init: the text IS the content here, there is no hover to wait
 * for. The codes render immediately and are replaced when the names arrive, so the cell is never
 * blank and never shifts from empty to full — and a code that the external EPPO service cannot
 * resolve simply stays a code.
 */
@Component({
  selector: 'app-species-names',
  template: `{{ text() }}`,
})
export class SpeciesNamesComponent {
  private readonly eppo = inject(EppoSpeciesService);
  private readonly resolvedNames = signal<Record<string, string>>({});

  readonly codes = input<string[] | null | undefined>();

  protected readonly text = computed(() => {
    const names = this.resolvedNames();
    return (this.codes() ?? []).map((code) => names[code] ?? code).join(', ');
  });

  constructor() {
    effect(async () => {
      const codes = this.codes();
      if (!codes?.length) return;

      const species = await this.eppo.resolveAll(codes);
      this.resolvedNames.update((names) => ({
        ...names,
        ...Object.fromEntries(species.map((s) => [s.code, this.eppo.label(s)])),
      }));
    });
  }
}
